import React, { useState, useEffect, useMemo } from "react";
import { Link } from 'react-router-dom';
import dayjs from 'dayjs';

// Material-UI Components
import {
  Box,
  Paper,
  Typography,
  Grid,
} from '@mui/material';

// Date Pickers (MUI X)
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DateCalendar } from '@mui/x-date-pickers/DateCalendar';

// Service
import StakeholderService from "../services/StakeholderService";

const Calendar = (props) => {
  // Stato per la data selezionata nel calendario
  const [dateValue, setDateValue] = useState(dayjs());
  const user = props.user;
  const isAdmin = props.user?.roles?.includes("ROLE_ADMIN") || false;

  // Stato per gli stakeholder (necessari per i compleanni)
  const [stakeholders, setStakeholders] = useState([]);

  // Stato per l'orario corrente
  const [currentTime, setCurrentTime] = useState(dayjs());

  // Funzione per il recupero degli stakeholder dai servizi
  const retrieveStakeholders = (sede) => {
    StakeholderService.getAll(sede)
      .then(response => {
        setStakeholders(response.data);
      })
      .catch(e => {
        console.error("Errore nel recupero degli stakeholder:", e);
      });
  };

  // useEffect per caricare gli stakeholder all'avvio del componente
  useEffect(() => {
    if (isAdmin) {
      retrieveStakeholders();
    } else if (user && user.sede) {
      retrieveStakeholders(user.sede);
    }
    // Se non c'è una sede specifica, puoi decidere di non caricare nulla
    // o chiamare retrieveStakeholders() senza parametri per caricarli tutti.

    // ... (le altre chiamate come retrieveTables rimangono qui se necessario)
  }, [isAdmin, user]); // L'effetto si attiva quando l'oggetto 'user' cambia

  // useEffect per aggiornare l'orario corrente ogni secondo
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(dayjs());
    }, 1000); // Aggiorna ogni secondo

    // Cleanup function: pulisce il timer quando il componente viene smontato
    return () => clearInterval(timer);
  }, []); // Esegue solo al mount e unmount

  // Gestione cambio data nel calendario
  const handleDate = (date) => {
    setDateValue(date);
  };

  // Logica per filtrare la lista dei compleanni (memoizzata per performance)
  const birthdayList = useMemo(() => {
    if (!dateValue || !stakeholders || stakeholders.length === 0) {
      return [];
    }
    const selectedDay = dateValue.date();
    const selectedMonth = dateValue.month() + 1; // getMonth() è 0-indexed

    return stakeholders.filter((sh) => {
      if (!sh.data_nascita) return false; // Assicurati che la data di nascita esista
      const birthDate = dayjs(sh.data_nascita);
      return birthDate.date() === selectedDay && birthDate.month() + 1 === selectedMonth;
    });
  }, [dateValue, stakeholders]); // Dipende da 'dateValue' e 'stakeholders'

  return (
    <Grid container spacing={2} sx={{ p: 2 }}> {/* Main Grid Container */}

      {/* Singola Colonna: Orario, Calendario, Compleanni */}
      <Grid item xs={12}> {/* Occupa sempre tutta la larghezza */}
        <Paper elevation={3} sx={{ p: 2, mb: 2 }}> {/* mb per margine sotto il Paper */}
          {/* Orario Corrente */}
          <Typography variant="h5" align="center" sx={{ mb: 2 }}>
            {currentTime.format('HH:mm:ss')}
          </Typography>

          {/* Calendario */}
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DateCalendar
              value={dateValue}
              onChange={handleDate}
              sx={{ width: '100%' }} // Rendi il calendario fluido
            />
          </LocalizationProvider>

          {/* Lista dei Compleanni */}
          <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid #eee' }}> {/* Margine e bordo superiore per separare */}
            <Typography sx={{ fontSize: 18 }} color="text.secondary" gutterBottom>
              🎂 Compleanni per il {dateValue.format("DD/MM")}
            </Typography>
            {
              birthdayList.length > 0 ? (
                birthdayList.map((b) => (
                  <Typography key={b.id} sx={{ marginTop: "10px", fontSize: 14 }} color="text.secondary">
                    {props.admin ? ( // Controlla se l'utente è admin
                      <Link to={`/dashboard/stakeholders/${b.id}`}>
                        {`${b.nome} ${b.cognome}`} {/* Se admin, mostra nome e cognome come link */}
                      </Link>
                    ) : (
                      // Altrimenti, mostra il codice senza link
                      <Typography component="span" sx={{ fontSize: 'inherit', color: 'inherit' }}>
                        {b.codice}
                      </Typography>
                    )}
                  </Typography>
                ))
              ) : (
                <Typography sx={{ marginTop: "10px", fontSize: 14 }} color="text.secondary">
                  Nessun compleanno per questa data.
                </Typography>
              )
            }
          </Box>
        </Paper>
      </Grid>
    </Grid>
  );
};

export default Calendar;